import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

/* Components */
import { Typography, Button, CircularProgress } from "@material-ui/core";
import { NavConfirmation } from "../../UserConfirmation";

/* Style */
import { useStyles } from "./styles";
import clsx from "clsx";

/* Utilities */
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import { useHistory } from "react-router-dom";
import usePos, { ResponseTypes } from "../Utilities/usePos";
import _ from "lodash";

/* State */
import useCurrentUser from "../../../hooks/useCurrentUser";
import { useSelector, useDispatch } from "react-redux";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import { addPayment, hydrateTransaction as hydrateTransactionThunk, clearTransaction } from "../../../state/slices/shiftSession/shiftSession";
import { PAYMENT_TYPES, EMPTY_GUID } from "../../../constants";
import { PAYMENT_GATEWAY } from "../../../constants";
import useRoamingTerminal from "../../../hooks/useRoamingTerminal";

const PayCredit = ({
  classes,
  onComplete,
  onFailedTransaction,
  onInterrupt,
}) => {
  const style = useStyles();
  const [loading, setLoading] = useState({
    loadingMessage: "Initializing Transaction",
    isLoading: true,
  });
  const currentUser = useCurrentUser();
  const [canCancel, setCanCancel] = useState(false);
  const [canShowPrompt, setCanShowPrompt] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const history = useHistory();
  const notificationStyle = useSelector((state) => {
    return {
      small: state.shiftSession.smallScreen,
      toastLocation: state.shiftSession.toastLocation,
    };
  });
  const { facilityID } = useCurrentFacility();
  const shiftInfo = useSelector((state) => state.shiftSession);
  const roamingCCTerminal = useRoamingTerminal(shiftInfo?.ccterminalId) ?? {};
  const hydratedPaymentStatus = useSelector(
    (state) => state.shiftSession.transaction.status
  );
  const dispatch = useDispatch();

  const { totalCost } = shiftInfo.transaction;

  const { processCreditPayment, cancelCredit, clearActiveTransaction, retryVerification, displayLines } = usePos({
    posTerminalID: shiftInfo?.ccterminalId,
    facilityID,
    cashierID: currentUser.UserID,
    onTransactionChanged: handleTransactionUpdate,
  });

  useEffect(() => {
    if (!hydratedPaymentStatus) return;
    if (hydratedPaymentStatus === ResponseTypes.PaymentVerificationFailed) {
      // Rehydrated into the terminal manual-review state.
      setVerificationFailed(true);
      setIsVerifying(false);
      setCanShowPrompt(false);
      setCanCancel(false);
      setLoading({ loadingMessage: "", isLoading: false });
    } else if (hydratedPaymentStatus === ResponseTypes.PaymentVerifying) {
      // Rehydrated mid-verification — show the verification screen, block cancel/re-attempt.
      setIsVerifying(true);
      setCanShowPrompt(true);
      setCanCancel(false);
      setLoading({ loadingMessage: "", isLoading: false });
    } else if (
      hydratedPaymentStatus === ResponseTypes.AwaitingPaymentCompleted ||
      hydratedPaymentStatus === ResponseTypes.InProgress
    ) {
        setCanCancel(true);
        setCanShowPrompt(true);
    }
  }, [hydratedPaymentStatus]);

  useEffect(() => {
    // Do not start a new payment when rehydrating into an already-active or terminal verification state.
    // Starting one would either be rejected by the backend or risk a duplicate charge.
    if (
      hydratedPaymentStatus === ResponseTypes.PaymentVerifying ||
      hydratedPaymentStatus === ResponseTypes.PaymentVerificationFailed
    ) {
      return;
    }
    initiateCreditPayment();
  }, []);

  async function handleTransactionUpdate(transaction) {
    try {
      setLoading({
        loadingMessage: "Please wait...",
        isLoading: true,
      });
      setCanCancel(false);

      // Payment outcome is unknown and being verified via an Elavon Transaction Inquiry.
      // Show the dedicated verification screen and prevent the cashier from attempting payment again.
      if (transaction.Status === ResponseTypes.PaymentVerifying) {
        setIsVerifying(true);
        setCanShowPrompt(true);
        setCanCancel(false);
        return;
      }

      // The inquiry could not verify the payment after all retries. The original charge may have
      // succeeded, so DO NOT route back to payment for a re-charge — show a terminal manual-review
      // screen whose only actions are an OK button that returns to the start screen and a retry button.
      if (transaction.Status === ResponseTypes.PaymentVerificationFailed) {
        setIsVerifying(false);
        setVerificationFailed(true);
        setCanShowPrompt(false);
        setCanCancel(false);
        return;
      }

      // Any terminal outcome ends the verification screen.
      setIsVerifying(false);

      if (transaction.Status === ResponseTypes.Failed) {
        setCanShowPrompt(false);

        enqueueSnackbar("Failed to process credit card please retry", {
          variant: "error",
          anchorOrigin: notificationStyle.toastLocation,
          fullwidth: notificationStyle.small,
        });

        // allowing the prompt flag to toggle
        setTimeout(() => {
          onFailedTransaction();
        }, 100);
      }

      if (transaction.Status === ResponseTypes.Complete) {
        setCanShowPrompt(false);
        dispatch(addPayment({ total: totalCost, paymentType: PAYMENT_TYPES.CreditCard }));
        // allowing the prompt flag to toggle
        setTimeout(() => {
          onComplete();
        }, 100);
      } else if (
        transaction.Status === ResponseTypes.Cancelled ||
        transaction.Status === ResponseTypes.Declined
      ) {
        setCanShowPrompt(false);

        const concatenatedDisplayLines = !transaction.DisplayLines[1]
          ? transaction.DisplayLines[0]
          : `${transaction.DisplayLines[0]} ${transaction.DisplayLines[1]}`;

        if (!_.isEmpty(concatenatedDisplayLines))
          enqueueSnackbar(concatenatedDisplayLines, {
            variant: "error",
            anchorOrigin: notificationStyle.toastLocation,
            fullwidth: notificationStyle.small,
          });

        // allowing the prompt flag to toggle
        setTimeout(() => {
          onInterrupt();
        }, 100);
      }
    } finally {
      // While verifying an unknown payment outcome (in-progress or terminal manual-review), keep the
      // relevant screen up and the cashier blocked from re-attempting — the status branches above
      // manage state instead.
      if (
        transaction.Status !== ResponseTypes.PaymentVerifying &&
        transaction.Status !== ResponseTypes.PaymentVerificationFailed
      ) {
        setLoading({
          loadingMessage: "",
          isLoading: false,
        });
        setCanCancel(true);
      }
    }
  }

  const initiateCreditPayment = async () => {
    try {
      await processCreditPayment(totalCost);
      setCanShowPrompt(true);
      setLoading({
        loadingMessage: "",
        isLoading: false,
      });
      setCanCancel(true);
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
        anchorOrigin: notificationStyle.toastLocation,
        fullwidth: notificationStyle.small,
      });
      if (hydratedPaymentStatus !== ResponseTypes.InProgress
        && hydratedPaymentStatus !== ResponseTypes.AwaitingPaymentCompleted
        && hydratedPaymentStatus !== ResponseTypes.PaymentVerifying ) {
          history.goBack();
      } else {
        setCanShowPrompt(true);
        setLoading({
          loadingMessage: "",
          isLoading: false,
        });
        setCanCancel(true);
      }
    }
  };

  const handleVerificationRetry = async () => {
    // Show the "Verifying payment status" screen immediately so the attendant gets visible feedback that
    // the retry is running. A definitive result drives the UI via the SignalR status change
    // (Complete -> summary, Failed -> back to payment); a still-unverified result re-publishes
    // PaymentVerificationFailed which returns us to the verification screen so they can retry again.
    setVerificationFailed(false);
    setIsVerifying(true);
    try {
      await retryVerification();
    } catch (error) {
      // The retry request itself failed — return to the verification-failed screen and surface the error.
      setIsVerifying(false);
      setVerificationFailed(true);
      enqueueSnackbar(error.message, {
        variant: "error",
        anchorOrigin: notificationStyle.toastLocation,
        fullwidth: notificationStyle.small,
      });
    }
  };

  const handleVerificationFailedAcknowledge = async () => {
    // Terminal state — close out the unverifiable transaction and return to start WITHOUT re-charging.
    // clearActiveTransaction deletes the tracking record (Elavon path sends a device CANCEL, not a
    // void/refund), so a payment that may have succeeded is left intact for manual reconciliation and
    // the screen does not reappear on refresh.
    setCanShowPrompt(false);
    try {
      await clearActiveTransaction();
    } catch (error) {
      // Even if clearing fails, don't trap the cashier on this screen — log and continue to start.
      console.error("Failed to clear unverifiable transaction", error);
    }
    dispatch(clearTransaction());
    history.push("/cashieredDevice/start");
  };

  const handleTransactionCancel = async () => {
    try {
      setCanShowPrompt(false);
      const result = await cancelCredit();
      if (result.success === false && result.transactionReference === EMPTY_GUID)
        dispatch(hydrateTransactionThunk(currentUser.UserID));
      else
        history.push("/cashieredDevice/payment");
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
        anchorOrigin: notificationStyle.toastLocation,
        fullwidth: notificationStyle.small,
      });
      setCanShowPrompt(true);
    }
  };

  return (
    <div className={classes.step} data-testid="v2-pay-credit">
      <NavConfirmation
        when={canShowPrompt}
        title="Warning!"
        message="Please complete your transaction before leaving the page."
        buttons={[
          {
            text: "Ok",
            attribute: "credit-navigation-ok-btn",
            state: false,
            color: "primary",
            order: 0,
          },
        ]}
      />
      {verificationFailed && (
        <div data-testid="payment-verification-failed" className={style.verificationFailed}>
          <Typography variant="h5" component="div" align="center" color="error">
            Payment status could not be verified
          </Typography>
          <div className={style.verificationFailedButtons}>
            <Button
              variant="outlined"
              color="primary"
              data-testid="verification-failed-retry-button"
              onClick={handleVerificationRetry}
            >
              Retry
            </Button>
            <Button
              variant="contained"
              color="primary"
              data-testid="verification-failed-ok-button"
              onClick={handleVerificationFailedAcknowledge}
            >
              OK
            </Button>
          </div>
        </div>
      )}
      {!verificationFailed && isVerifying && (
        <div data-testid="payment-verifying" className={style.verifying}>
          <CircularProgress size="40px" />
          <Typography variant="h5" component="div" align="center">
            Verifying payment status
          </Typography>
          <Typography variant="h6" component="div" align="center">
            Please wait while we confirm your payment. Do not attempt to submit another payment at this time.
          </Typography>
        </div>
      )}
      {!verificationFailed && !isVerifying && loading.isLoading && (
        <>
          <CircularProgress size="40px" />
          <Typography
            variant="h5"
            component="div"
            align="center"
            data-testid="circular-progress-loader"
          >
            {loading.loadingMessage}
          </Typography>
        </>
      )}
      {!verificationFailed && !isVerifying && !loading.isLoading && (
        <>
          <div>
            <Typography
              variant="h5"
              component="p"
              align="center"
              data-testid="terminal-display-line-1"
            >
              {displayLines.displayLine1}
            </Typography>
            <Typography
              variant="h5"
              component="p"
              align="center"
              data-testid="terminal-display-line-2"
            >
              {displayLines.displayLine2}
            </Typography>
          </div>
          <Typography
            variant="h4"
            component="div"
            align="center"
            data-testid="total-cost"
            className={clsx(classes.totalCost, style.payCreditTotalCost)}
            role="caption"
          >
            {`$${totalCost?.toFixed(2)}`}
          </Typography>
          {canCancel && roamingCCTerminal?.creditCardGatewayId != PAYMENT_GATEWAY.PLANET && (
            <Button
              variant="contained"
              color="secondary"
              data-testid="transaction-cancel-button"
              className={classes.transactionCancelButton}
              onClick={handleTransactionCancel}
            >
              Cancel
            </Button>
          )}
        </>
      )}
    </div>
  );
};

PayCredit.defaultProps = {
  onComplete: () => {},
  onFailedTransaction: () => {},
  onInterrupt: () => {},
};

PayCredit.propTypes = {
  onComplete: PropTypes.func,
  onFailedTransaction: PropTypes.func,
  onInterrupt: PropTypes.func,
};

export default PayCredit;
